import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { AuthContext } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import { EditDueDateButton } from "./EditDueDate";
import TaskTatPanel from "../../components/tasks/TaskTatPanel";
import TaskParticipantsPanel from "../../components/tasks/TaskParticipantsPanel";
import TaskApprovalPanel from "../../components/tasks/TaskApprovalPanel";
import {
  viewAuthenticatedFileInNewTab,
  downloadAuthenticatedFile,
  getFileAccessErrorMessage,
} from "../../utils/fileAccess";
import { Button, StatusBadge, PriorityBadge, LoadingState, ErrorState } from "../../components/ui";
import { statusPillClass } from "./StatusChips";
import { getTatInfo } from "./tatUtils";
import CompleteTicketModal from "./CompleteTicketModal";
import { levelLabel } from "./dataAnalystTat";
import {
  FiArrowLeft, FiClipboard, FiClock, FiHash, FiTag, FiPaperclip, FiSend,
  FiMoreVertical, FiUpload, FiUserPlus, FiRefreshCw, FiMessageSquare,
  FiFile, FiEye, FiDownload, FiTrash2, FiCheckCircle, FiUserCheck,
  FiUsers, FiPlusCircle, FiChevronRight, FiFolder, FiLayers,
  FiActivity, FiUser, FiGrid, FiFlag,
} from "react-icons/fi";
import { taskDisplayTitle, taskDescriptionText } from "../../utils/taskDisplay";
import "./TaskDetailPage.css";

const ATTACHMENT_TYPES = [
  "REQUIREMENT", "SPECIFICATION", "CHANGE_REQUEST", "SCREENSHOT",
  "ERROR_REPORT", "TESTING_EVIDENCE", "APPROVAL_DOCUMENT", "SUPPORTING_DOCUMENT", "OTHER",
];

const REMARK_TYPES = [
  { value: "general", label: "General" },
  { value: "progress", label: "Progress" },
  { value: "issue", label: "Issue" },
  { value: "resolution", label: "Resolution" },
];

// tasks.tat_source values (backend phase-2c + phase-61) -> readable labels.
const TAT_SOURCE_LABELS = {
  DATA_ANALYST_MANUAL: "Set manually on this ticket",
  TASK_OVERRIDE: "Task override",
  PROJECT_MODULE: "Project module",
  PROJECT: "Project",
  TASK_TYPE_PRIORITY: "Task type & priority",
  DEPARTMENT: "Department",
  IT_SUBCATEGORY: "Subcategory default",
  GLOBAL: "Global default",
};

function formatDateTime(dt) {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function initials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  return s.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext) || {};
  const permissionCtx = usePermissions() || {};
  const basePath = `/${user?.role || ""}`;

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [remarkText, setRemarkText] = useState("");
  const [remarkType, setRemarkType] = useState("general");
  const [submittingRemark, setSubmittingRemark] = useState(false);
  const [remarkError, setRemarkError] = useState("");

  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentType, setAttachmentType] = useState("SUPPORTING_DOCUMENT");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const [transitions, setTransitions] = useState([]);
  const [loadingTransitions, setLoadingTransitions] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [transitionRemark, setTransitionRemark] = useState("");
  const [executingTransition, setExecutingTransition] = useState(false);
  const [transitionError, setTransitionError] = useState("");
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const assignmentRef = useRef(null);
  const tatRef = useRef(null);
  const attachmentsRef = useRef(null);
  const remarksRef = useRef(null);
  const timelineRef = useRef(null);
  const statusRef = useRef(null);
  const fileInputRef = useRef(null);
  const remarkInputRef = useRef(null);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (err) {
      console.error("Load task error:", err);
      setLoadError(err?.response?.data?.message || "Failed to load this task.");
    } finally {
      setLoading(false);
    }
  };

  const loadTransitions = async () => {
    if (!taskId) return;
    setLoadingTransitions(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/allowed-transitions`);
      setTransitions(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Load allowed transitions error:", err);
      setTransitions([]);
    } finally {
      setLoadingTransitions(false);
    }
  };

  const loadAttachments = async () => {
    if (!taskId) return;
    setLoadingAttachments(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/attachments`);
      setAttachments(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Load attachments error:", err);
    } finally {
      setLoadingAttachments(false);
    }
  };

  useEffect(() => {
    load();
    loadAttachments();
    loadTransitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const scrollToSection = (ref, focusRef) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (focusRef) window.setTimeout(() => focusRef.current?.focus(), 350);
  };

  // Deep-link support for the My Tickets table's "Update Status" quick
  // action (?action=status) -- reuses this page's own EXISTING status
  // workflow UI/API, never a second status-change surface. Runs once the
  // task itself has loaded so scrollToSection has a real element to target.
  useEffect(() => {
    if (task && searchParams.get("action") === "status") {
      setShowStatusPanel(true);
      scrollToSection(statusRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  const openTransition = (transition) => {
    setTransitionError("");
    setPendingTransition(transition);
    setTransitionRemark("");
  };

  const cancelTransition = () => {
    setPendingTransition(null);
    setTransitionRemark("");
    setTransitionError("");
  };

  const confirmTransition = async () => {
    if (!task || !pendingTransition) return;
    if (pendingTransition.requires_remark && !transitionRemark.trim()) {
      setTransitionError("A remark is required for this status change.");
      return;
    }
    setExecutingTransition(true);
    setTransitionError("");
    try {
      await api.post(`/tasks/${task.id}/transitions/${pendingTransition.transition_id}`, {
        remark: transitionRemark.trim() || undefined,
      });
      setPendingTransition(null);
      setTransitionRemark("");
      setShowStatusPanel(false);
      await Promise.all([load(), loadTransitions()]);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "TASK_TRANSITION_STALE") {
        setTransitionError("This task's status changed since you opened it. Refreshing…");
        await Promise.all([load(), loadTransitions()]);
      } else {
        setTransitionError(data?.message || "Failed to update status.");
      }
    } finally {
      setExecutingTransition(false);
    }
  };

  const handleAttachmentUpload = async () => {
    if (!task || !attachmentFile) return;
    setUploadingAttachment(true);
    setAttachmentError("");
    try {
      const formData = new FormData();
      formData.append("file", attachmentFile);
      formData.append("attachment_type", attachmentType);
      await api.post(`/tasks/${task.id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachmentFile(null);
      await loadAttachments();
    } catch (err) {
      setAttachmentError(getFileAccessErrorMessage(err));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentView = async (attachment) => {
    try {
      await viewAuthenticatedFileInNewTab(`/tasks/${task.id}/attachments/${attachment.id}/view`);
    } catch (err) {
      setAttachmentError(getFileAccessErrorMessage(err));
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    try {
      await downloadAuthenticatedFile(`/tasks/${task.id}/attachments/${attachment.id}/download`, attachment.original_name);
    } catch (err) {
      setAttachmentError(getFileAccessErrorMessage(err));
    }
  };

  const handleAttachmentDelete = async (attachment) => {
    if (!window.confirm(`Delete "${attachment.original_name}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}/attachments/${attachment.id}`);
      await loadAttachments();
    } catch (err) {
      setAttachmentError(getFileAccessErrorMessage(err));
    }
  };

  const addRemark = async () => {
    if (!task || !remarkText.trim() || !user?.id) return;
    setSubmittingRemark(true);
    setRemarkError("");
    try {
      await api.post(`/tasks/${task.id}/remarks`, {
        remark: remarkText.trim(),
        remark_type: remarkType,
        created_by: user.id,
      });
      setRemarkText("");
      await load();
    } catch (err) {
      setRemarkError(err?.response?.data?.message || "Failed to add remark.");
    } finally {
      setSubmittingRemark(false);
    }
  };

  const tat = useMemo(() => getTatInfo(task || {}), [task]);

  const remarks = useMemo(() => {
    const arr = task?.remarks || [];
    return [...arr].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [task]);

  const timelineEvents = useMemo(() => {
    if (!task) return [];
    const history = (task.status_history || []).map((h, i) => ({
      key: `history-${h.changed_at}-${i}`,
      kind: "status",
      at: h.changed_at,
      toCode: h.to_status_code || h.to_status_name || h.to_status,
      fromLabel: h.from_status_name || h.from_status_code || h.from_status || "—",
      toLabel: h.to_status_name || h.to_status_code || h.to_status,
      transitionName: h.transition_name,
      changeSource: h.change_source,
      actor: h.changed_by?.name,
      note: h.note,
    }));
    const created = {
      key: "created",
      kind: "created",
      at: task.created_at,
      toLabel: "Task Created",
      actor: task.created_by?.name,
    };
    return [created, ...history];
  }, [task]);

  if (loading && !task) {
    return (
      <div className="tdp-page">
        <LoadingState message="Loading task…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="tdp-page">
        <ErrorState
          title="Unable to load task"
          message={loadError}
          action={
            <Button variant="secondary" icon={<FiArrowLeft />} onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  if (!loading && !task) {
    return (
      <div className="tdp-page">
        <ErrorState
          title="Task not found"
          message="This task may have been removed, or you may not have access to it."
          action={
            <Button variant="secondary" icon={<FiArrowLeft />} onClick={() => navigate(`${basePath}/home`)}>
              Back to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const isOverdue = tat.tone === "red";
  const hasTat = !!task.due_at && tat.label !== "—";
  const tatAllowance = task.tat?.value && task.tat?.unit
    ? `${task.tat.value} ${Number(task.tat.value) === 1 ? task.tat.unit.replace(/s$/, "") : task.tat.unit}`
    : "";
  // Own Tickets are stored with priority NULL; their TAT level is the
  // closest thing to a priority the creator chose.
  const displayPriority = task.priority || task.tat?.level || null;
  const tatLevelLabel = task.tat?.level ? levelLabel(task.tat.level) : "";
  // Phase 66: a Data Analyst Own Ticket may carry several subcategories /
  // schools -- show every one, never just the primary.
  const subCategoryNames = (task.sub_categories?.length ? task.sub_categories : (task.sub_category?.name ? [task.sub_category] : [])).map((s) => s.name);
  const schoolNames = (task.schools?.length ? task.schools : (task.school?.name ? [task.school] : [])).map((s) => s.name);
  const subCategoryLabel = subCategoryNames.length > 1 ? "Sub-Categories" : "Sub-Category";
  const renderNameList = (names) => (names.length > 1
    ? <ul className="tdp-name-list">{names.map((n) => <li key={n}>{n}</li>)}</ul>
    : names[0] || "—");
  const tatSourceLabel = TAT_SOURCE_LABELS[task.tat?.source] || "";
  // Same normalized role check as OwnTicketModal: login carries `role`
  // ("data-analyst"), GET /auth/me only `role_name` ("Data Analyst").
  const normalizedRole = String(user?.role ?? user?.role_name ?? user?.roleName ?? "")
    .trim().toLowerCase().replace(/[\s_]+/g, "-");
  const isDataAnalyst = normalizedRole === "data-analyst";
  const actorId = Number(user?.id);
  const isTaskOwner =
    Number(task.assigned_to?.id) === actorId ||
    Number(task.created_by?.id) === actorId;
  const isCompleted = String(task.status || "").toLowerCase() === "completed";

  return (
    <div className="tdp-page">
      {/* 1. HEADER */}
      <div className="tdp-header ui-card">
        <button type="button" className="tdp-back-link" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>

        <div className="tdp-header-row">
          <div className="tdp-header-left">
            <div className={`tdp-icon tdp-icon-${(task.priority || "medium").toLowerCase()}`}>
              <FiClipboard />
            </div>
            <div>
              <div className="tdp-task-id"><FiHash />TASK #{task.id}</div>
              <h1 className="tdp-title" title={taskDisplayTitle(task)}>{taskDisplayTitle(task)}</h1>
              <div className="tdp-category-line">
                <FiTag />
                <span>{task.category?.name || "Uncategorized"}</span>
                {task.sub_category?.name && <><FiChevronRight className="tdp-sep" /><span>{task.sub_category.name}</span></>}
              </div>
              {task.project && (
                <div className="tdp-project-line">
                  <span className="tdp-chip tdp-chip-project"><FiFolder /> {task.project.name}</span>
                  {task.project_module && (
                    <span className="tdp-chip tdp-chip-module"><FiLayers /> {task.project_module.name}</span>
                  )}
                </div>
              )}
              <div className="tdp-meta-line">
                <span><span className="tdp-meta-label">Created:</span> {formatDateTime(task.created_at)}</span>
                <span><span className="tdp-meta-label">Updated:</span> {formatDateTime(task.updated_at)}</span>
              </div>
            </div>
          </div>

          <div className="tdp-header-actions">
            {isTaskOwner && (
              <Button
                variant={isCompleted ? "secondary" : "primary"}
                icon={<FiCheckCircle />}
                onClick={() => setShowCompleteModal(true)}
              >
                {isCompleted ? "Edit Completion" : "Mark Completed"}
              </Button>
            )}
            <Can moduleKey="tasks" actionKey="change_status">
              <Button
                variant="primary"
                icon={<FiCheckCircle />}
                onClick={() => { setShowStatusPanel(true); scrollToSection(statusRef); }}
              >
                Change Status
              </Button>
            </Can>
            <Can moduleKey="task_assignments" actionKey="reassign">
              <Button
                variant="secondary"
                icon={<FiUserPlus />}
                onClick={() => scrollToSection(assignmentRef)}
              >
                Assign
              </Button>
            </Can>

            <Dropdown align="end" className="tdp-more-menu">
              <Dropdown.Toggle as="button" className="ui-icon-btn" aria-label="More actions" title="More actions">
                <FiMoreVertical />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Can moduleKey="tasks" actionKey="add_remarks">
                  <Dropdown.Item onClick={() => scrollToSection(remarksRef, remarkInputRef)}>
                    <FiMessageSquare className="tdp-menu-icon" /> Add Remark
                  </Dropdown.Item>
                </Can>
                <Can moduleKey="tasks" actionKey="upload_attachment">
                  <Dropdown.Item onClick={() => scrollToSection(attachmentsRef, fileInputRef)}>
                    <FiUpload className="tdp-menu-icon" /> Upload File
                  </Dropdown.Item>
                </Can>
                <Dropdown.Item onClick={() => scrollToSection(timelineRef)}>
                  <FiClock className="tdp-menu-icon" /> View Timeline
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* 2. STATUS SUMMARY BAR */}
      <div className="tdp-statusbar" ref={statusRef}>
        <div className="tdp-stat">
          <span className="tdp-stat-label"><FiActivity className="tdp-stat-icon" />Current Status</span>
          <div className="tdp-stat-value"><StatusBadge status={task.status} /></div>
          {isCompleted && task.completed_at && (
            <span className="tdp-stat-sub">on {formatDateTime(task.completed_at)}</span>
          )}
        </div>

        <div className={`tdp-stat tdp-stat-tat tdp-stat-tat-${hasTat ? tat.tone : "gray"} ${isOverdue ? "tdp-stat-danger" : ""}`}>
          <span className="tdp-stat-label"><FiClock className="tdp-stat-icon" />TAT Status</span>
          <div className={`tdp-stat-value tdp-tat-value tdp-tat-${hasTat ? tat.tone : "gray"}`}>
            {!hasTat ? "No TAT set" : isOverdue ? <strong>{tat.label.toUpperCase()}</strong> : tat.label}
          </div>
          {hasTat ? (
            <span className="tdp-stat-sub">
              {tatAllowance && <>{tatAllowance} · </>}Due {formatDateTime(task.due_at)}
            </span>
          ) : (
            <span className="tdp-stat-sub">No due date was recorded for this task</span>
          )}
        </div>

        <div className="tdp-stat">
          <span className="tdp-stat-label"><FiUserCheck className="tdp-stat-icon" />Assigned To</span>
          <div className="tdp-stat-value tdp-person">
            <span className="tdp-avatar">{initials(task.assigned_to?.name)}</span>
            <span className="tdp-person-name">{task.assigned_to?.name || "Unassigned"}</span>
          </div>
        </div>

        <div className="tdp-stat">
          <span className="tdp-stat-label"><FiUser className="tdp-stat-icon" />Created By</span>
          <div className="tdp-stat-value tdp-person">
            <span className="tdp-avatar tdp-avatar-muted">{initials(task.created_by?.name)}</span>
            <span className="tdp-person-name">{task.created_by?.name || "—"}</span>
          </div>
        </div>

        <div className="tdp-stat">
          <span className="tdp-stat-label"><FiGrid className="tdp-stat-icon" />Task Type</span>
          <div className="tdp-stat-value">{task.task_type?.name || "IT Support"}</div>
        </div>

        <div className="tdp-stat">
          <span className="tdp-stat-label"><FiFlag className="tdp-stat-icon" />Priority</span>
          <div className="tdp-stat-value"><PriorityBadge priority={displayPriority} /></div>
          {!task.priority && task.tat?.level && <span className="tdp-stat-sub">From TAT level</span>}
        </div>
      </div>

      {/* 8. STATUS WORKFLOW ACTIONS (inline, triggered from header/status bar) */}
      <Can moduleKey="tasks" actionKey="change_status">
        {showStatusPanel && (
          <div className="tdp-workflow ui-card">
            <div className="ui-card-head">
              <h3 className="ui-card-title">Status Workflow</h3>
              <button type="button" className="tdp-panel-close" aria-label="Close" onClick={() => { setShowStatusPanel(false); cancelTransition(); }}>×</button>
            </div>
            <div className="ui-card-body">
              {!pendingTransition ? (
                transitions.length ? (
                  <div className="tdp-workflow-row">
                    <select
                      className="ui-filter-select tdp-workflow-select"
                      defaultValue=""
                      aria-label="Select a status change"
                      onChange={(e) => {
                        const t = transitions.find((x) => String(x.transition_id) === e.target.value);
                        if (t) openTransition(t);
                      }}
                    >
                      <option value="" disabled>Select a status change…</option>
                      {transitions.map((t) => (
                        <option key={t.transition_id} value={t.transition_id}>
                          {t.name} → {t.to_status?.name}{t.requires_remark ? " (remark required)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="tdp-workflow-empty">
                    {loadingTransitions ? "Loading available actions…" : "No status changes are available for this task right now."}
                  </div>
                )
              ) : (
                <div className="tdp-workflow-confirm">
                  <div className="tdp-workflow-confirm-head">
                    Move to{" "}
                    <span className={statusPillClass(pendingTransition.to_status?.code || pendingTransition.to_status?.name)}>
                      {pendingTransition.to_status?.name}
                    </span>
                    {pendingTransition.requires_remark && <span className="tdp-required"> (remark required)</span>}
                  </div>
                  <textarea
                    className="tdp-textarea"
                    rows={2}
                    value={transitionRemark}
                    onChange={(e) => setTransitionRemark(e.target.value)}
                    placeholder={pendingTransition.requires_remark ? "Remark (required)…" : "Remark (optional)…"}
                  />
                  {transitionError && <div className="tdp-inline-error">{transitionError}</div>}
                  <div className="tdp-workflow-actions">
                    <Button variant="primary" size="sm" onClick={confirmTransition} loading={executingTransition}>Confirm</Button>
                    <Button variant="secondary" size="sm" onClick={cancelTransition} disabled={executingTransition}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Can>

      {/* MAIN GRID: 70% main / 30% sticky summary */}
      <div className="tdp-grid">
        <div className="tdp-main">
          {/* 4. TASK DETAILS */}
          <div className="ui-card">
            <div className="ui-card-head"><h3 className="ui-card-title">Task Details</h3></div>
            <div className="ui-card-body">
              <div className="tdp-description">
                <span className="tdp-field-label">Description</span>
                <p className={`tdp-field-value ${taskDescriptionText(task) ? "" : "tdp-field-empty"}`}>
                  {taskDescriptionText(task) || "—"}
                </p>
              </div>

              <h4 className="tdp-details-heading">Overview</h4>
              <div className="tdp-details-grid">
                <div className="tdp-field">
                  <span className="tdp-field-label">Requested For</span>
                  <span className="tdp-field-value">{task.requested_for?.name || "—"}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Category</span>
                  <span className="tdp-field-value">{task.category?.name || "—"}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">{subCategoryLabel}</span>
                  <span className="tdp-field-value">{renderNameList(subCategoryNames)}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Task Type</span>
                  <span className="tdp-field-value">{task.task_type?.name || "IT Support"}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Source</span>
                  <span className="tdp-field-value">
                    {task.form_source ? `Form Submission — ${task.form_source.form_name}` : "Manual Entry"}
                  </span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Unit</span>
                  <span className="tdp-field-value">{task.unit?.name || "—"}</span>
                </div>
                {/* Phase 60: only Data Analyst Own Tickets can carry a
                    School, so every other ticket keeps its layout. */}
                {schoolNames.length > 0 && (
                  <div className="tdp-field">
                    <span className="tdp-field-label">{schoolNames.length > 1 ? "Schools" : "School"}</span>
                    <span className="tdp-field-value">{renderNameList(schoolNames)}</span>
                  </div>
                )}
                <div className="tdp-field">
                  <span className="tdp-field-label">Priority</span>
                  <span className="tdp-field-value"><PriorityBadge priority={displayPriority} /></span>
                </div>
                {task.department && (
                  <div className="tdp-field">
                    <span className="tdp-field-label">Department</span>
                    <span className="tdp-field-value">{task.department.name}</span>
                  </div>
                )}
                {task.project && (
                  <div className="tdp-field">
                    <span className="tdp-field-label">Project</span>
                    <span className="tdp-field-value">{task.project.name}</span>
                  </div>
                )}
                {task.project_module && (
                  <div className="tdp-field">
                    <span className="tdp-field-label">Module</span>
                    <span className="tdp-field-value">{task.project_module.name}</span>
                  </div>
                )}
              </div>

              {/* Data Analyst only -- they set TAT manually on their Own
                  Tickets, so a missing TAT reads as "Not set" instead of
                  silently disappearing. Values come straight from the API
                  (due_at + the applied/manual `tat` summary). */}
              {isDataAnalyst && (<>
              <h4 className="tdp-details-heading">Turnaround Time</h4>
              <div className="tdp-details-grid">
                <div className="tdp-field">
                  <span className="tdp-field-label">TAT</span>
                  <span className={`tdp-field-value ${tatAllowance ? "" : "tdp-field-empty"}`}>{tatAllowance || "Not set"}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">TAT Level</span>
                  <span className={`tdp-field-value ${tatLevelLabel ? "" : "tdp-field-empty"}`}>{tatLevelLabel || "Not set"}</span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Due Date</span>
                  <span className={`tdp-field-value ${task.due_at ? (isOverdue ? "tdp-text-danger" : "") : "tdp-field-empty"}`}>
                    {task.due_at ? formatDateTime(task.due_at) : "Not set"}
                  </span>
                </div>
                <div className="tdp-field">
                  <span className="tdp-field-label">TAT Status</span>
                  <span className={`tdp-field-value ${hasTat ? `tdp-tat-value tdp-tat-${tat.tone}` : "tdp-field-empty"}`}>
                    {hasTat ? tat.label : "No TAT set"}
                  </span>
                </div>
                {tatSourceLabel && (
                  <div className="tdp-field">
                    <span className="tdp-field-label">TAT Source</span>
                    <span className="tdp-field-value">{tatSourceLabel}</span>
                  </div>
                )}
              </div>
              </>)}

              {task.form_source && (
                <div className="tdp-form-source">
                  <div className="tdp-field-label">Source Form / UAT Reference</div>
                  <div className="tdp-form-source-body">
                    {(task.form_source.test_case_id || task.form_source.test_case_title) && (
                      <div><strong>Test Case:</strong> {task.form_source.test_case_id || task.form_source.test_case_title}</div>
                    )}
                    {task.form_source.result_status && (
                      <div><strong>Result at creation:</strong> {task.form_source.result_status}</div>
                    )}
                    {task.form_source.expected_result && <div><strong>Expected:</strong> {task.form_source.expected_result}</div>}
                    {task.form_source.actual_result && <div><strong>Actual:</strong> {task.form_source.actual_result}</div>}
                    {task.form_source.severity && <div><strong>Severity:</strong> {task.form_source.severity}</div>}
                    <a
                      className="tdp-link"
                      href={`${basePath}/form-submissions/${task.form_source.form_submission_id}`}
                    >
                      View source submission →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 5 & 6. PEOPLE & ASSIGNMENT MANAGEMENT + PERFORMER TABS */}
          <div className="tdp-section" ref={assignmentRef}>
            <TaskParticipantsPanel
              taskId={task.id}
              assignedTo={task.assigned_to?.id}
              assignedToName={task.assigned_to?.name}
              taskType={task.task_type}
              readOnly={false}
            />
          </div>

          <div className="tdp-section">
            <TaskApprovalPanel taskId={task.id} approvalSummary={task.approval} />
          </div>

          {/* 7. TAT / SLA MANAGEMENT */}
          <div className="ui-card tdp-section" ref={tatRef}>
            <div className="ui-card-head"><h3 className="ui-card-title"><FiClock className="tdp-heading-icon" />Turnaround Time (TAT)</h3></div>
            <div className="ui-card-body">
              <TaskTatPanel taskId={task.id} onChanged={load} />
            </div>
          </div>

          {/* 9. ATTACHMENTS */}
          <div className="ui-card tdp-section" ref={attachmentsRef}>
            <div className="ui-card-head">
              <h3 className="ui-card-title"><FiPaperclip className="tdp-heading-icon" />Attachments</h3>
              <span className="ui-badge ui-badge-gray">{attachments.length} total</span>
            </div>
            <div className="ui-card-body">
              {loadingAttachments ? (
                <LoadingState message="Loading attachments…" />
              ) : attachments.length ? (
                <div className="tdp-table-wrap">
                  <table className="ui-table tdp-attachments-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Uploaded By</th>
                        <th>Date</th>
                        <th className="ui-th-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((a) => (
                        <tr key={a.id}>
                          <td>
                            <div className="tdp-file-name"><FiFile className="tdp-file-icon" />{a.original_name}{a.version_no > 1 && <span className="ui-badge ui-badge-blue tdp-version-badge">v{a.version_no}</span>}</div>
                            <div className="tdp-file-size">{(a.file_size / 1024).toFixed(0)} KB</div>
                          </td>
                          <td><span className="ui-badge ui-badge-gray">{a.attachment_type}</span></td>
                          <td>{a.uploaded_by_name || "—"}</td>
                          <td>{formatDateTime(a.uploaded_at)}</td>
                          <td className="ui-td-center">
                            <div className="tdp-row-actions">
                              <Can moduleKey="tasks" actionKey="view_attachment">
                                <button type="button" className="ui-icon-btn" title="View" aria-label={`View ${a.original_name}`} onClick={() => handleAttachmentView(a)}><FiEye /></button>
                              </Can>
                              <Can moduleKey="tasks" actionKey="download_attachment">
                                <button type="button" className="ui-icon-btn" title="Download" aria-label={`Download ${a.original_name}`} onClick={() => handleAttachmentDownload(a)}><FiDownload /></button>
                              </Can>
                              <Can moduleKey="tasks" actionKey="delete_attachment">
                                <button type="button" className="ui-icon-btn ui-icon-btn-danger" title="Delete" aria-label={`Delete ${a.original_name}`} onClick={() => handleAttachmentDelete(a)}><FiTrash2 /></button>
                              </Can>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ui-state">
                  <div className="ui-state-icon ui-state-icon-neutral"><FiPaperclip size={18} /></div>
                  <h5>No attachments uploaded</h5>
                  <p>Files shared on this task will show up here.</p>
                </div>
              )}

              <Can moduleKey="tasks" actionKey="upload_attachment">
                <div
                  className={`tdp-dropzone ${dragActive ? "tdp-dropzone-active" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) setAttachmentFile(f);
                  }}
                >
                  <FiUpload className="tdp-dropzone-icon" />
                  <div className="tdp-dropzone-text">
                    {attachmentFile ? (
                      <strong>{attachmentFile.name}</strong>
                    ) : (
                      <>Drag &amp; drop a file here, or <button type="button" className="tdp-link-btn" onClick={() => fileInputRef.current?.click()}>choose a file</button></>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="tdp-file-input"
                    onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  />
                  <select
                    className="ui-filter-select"
                    value={attachmentType}
                    onChange={(e) => setAttachmentType(e.target.value)}
                    aria-label="Attachment type"
                  >
                    {ATTACHMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<FiUpload />}
                    onClick={handleAttachmentUpload}
                    disabled={!attachmentFile}
                    loading={uploadingAttachment}
                  >
                    Upload
                  </Button>
                </div>
                {attachmentError && <div className="tdp-inline-error">{attachmentError}</div>}
              </Can>
            </div>
          </div>

          {/* 10. REMARKS / COMMENTS */}
          <div className="ui-card tdp-section" ref={remarksRef}>
            <div className="ui-card-head">
              <h3 className="ui-card-title"><FiMessageSquare className="tdp-heading-icon" />Activity &amp; Remarks</h3>
              <span className="ui-badge ui-badge-gray">{remarks.length} total</span>
            </div>
            <div className="ui-card-body">
              {remarks.length ? (
                <div className="tdp-chat">
                  {remarks.map((r) => (
                    <div key={r.id} className="tdp-chat-item">
                      <span className="tdp-avatar">{initials(r.created_by?.name)}</span>
                      <div className="tdp-chat-bubble">
                        <div className="tdp-chat-head">
                          <strong>{r.created_by?.name || "—"}</strong>
                          <span className="ui-badge ui-badge-gray tdp-chat-type">{r.remark_type}</span>
                          <span className="tdp-chat-time">{formatDateTime(r.created_at)}</span>
                        </div>
                        <div className="tdp-chat-text">{r.remark}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ui-state">
                  <div className="ui-state-icon ui-state-icon-neutral"><FiMessageSquare size={18} /></div>
                  <h5>No remarks yet</h5>
                  <p>Updates and internal notes on this task will appear here.</p>
                </div>
              )}

              <Can moduleKey="tasks" actionKey="add_remarks">
                <div className="tdp-composer">
                  <select
                    className="ui-filter-select"
                    value={remarkType}
                    onChange={(e) => setRemarkType(e.target.value)}
                    aria-label="Comment type"
                  >
                    {REMARK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input
                    ref={remarkInputRef}
                    className="ui-search-input tdp-composer-input"
                    style={{ paddingLeft: 12 }}
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    placeholder="Write an update… (Ctrl+Enter to send)"
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && remarkText.trim() && !submittingRemark) addRemark();
                    }}
                  />
                  <Button
                    variant="primary"
                    icon={<FiSend />}
                    onClick={addRemark}
                    disabled={!remarkText.trim()}
                    loading={submittingRemark}
                  >
                    Add Comment
                  </Button>
                </div>
                {remarkError && <div className="tdp-inline-error">{remarkError}</div>}
              </Can>
            </div>
          </div>

          {/* 11. ACTIVITY TIMELINE */}
          <div className="ui-card tdp-section" ref={timelineRef}>
            <div className="ui-card-head"><h3 className="ui-card-title">Status Timeline</h3></div>
            <div className="ui-card-body">
              {timelineEvents.length ? (
                <div className="tdp-timeline">
                  {timelineEvents.map((e) => (
                    <div className="tdp-timeline-item" key={e.key}>
                      <span className={`tdp-timeline-dot tdp-timeline-dot-${e.kind === "created" ? "blue" : statusToneFromCode(e.toCode)}`} />
                      <div className="tdp-timeline-content">
                        <div className="tdp-timeline-head">
                          {e.kind === "created" ? (
                            <b>Task Created</b>
                          ) : (
                            <>
                              <span className="tdp-timeline-muted">{e.fromLabel} →</span>{" "}
                              <b className="text-capitalize">{e.toLabel}</b>
                              {e.transitionName && <span className="ui-badge ui-badge-gray tdp-timeline-badge">{e.transitionName}</span>}
                            </>
                          )}
                        </div>
                        <div className="tdp-timeline-sub">
                          {e.actor ? `${e.actor} • ` : ""}{formatDateTime(e.at)}
                          {e.changeSource ? ` • ${e.changeSource}` : ""}
                        </div>
                        {e.note && <div className="tdp-timeline-note">"{e.note}"</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ui-state">
                  <div className="ui-state-icon ui-state-icon-neutral"><FiClock size={18} /></div>
                  <h5>No history found</h5>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. RIGHT SIDE TASK SUMMARY PANEL */}
        <aside className="tdp-side">
          <div className="ui-card tdp-summary">
            <div className="ui-card-head"><h3 className="ui-card-title">Task Summary</h3></div>
            <div className="ui-card-body">
              <div className="tdp-summary-group">
                <span className="tdp-summary-heading">People</span>
                <div className="tdp-field"><span className="tdp-field-label">Created By</span><span className="tdp-field-value">{task.created_by?.name || "—"}</span></div>
                <div className="tdp-field"><span className="tdp-field-label">Assigned To</span><span className="tdp-field-value">{task.assigned_to?.name || "Unassigned"}</span></div>
                <div className="tdp-field"><span className="tdp-field-label">Requested For</span><span className="tdp-field-value">{task.requested_for?.name || "—"}</span></div>
              </div>

              <div className="tdp-summary-group">
                <span className="tdp-summary-heading">Dates</span>
                <div className="tdp-field"><span className="tdp-field-label">Created Date</span><span className="tdp-field-value">{formatDateTime(task.created_at)}</span></div>
                <div className="tdp-field">
                  <span className="tdp-field-label">Due Date</span>
                  <span className={`tdp-field-value ${isOverdue ? "tdp-text-danger" : ""}`}>
                    {formatDateTime(task.due_at)}
                    {/* Assignee-only, open tasks only (PATCH /tasks/:id/due-date). */}
                    <EditDueDateButton
                      task={task}
                      user={user}
                      can={permissionCtx?.can}
                      onUpdated={(dueAt) => setTask((prev) => (prev ? { ...prev, due_at: dueAt } : prev))}
                    />
                  </span>
                </div>
                {/* "Last Updated" is the system audit timestamp (last DB
                    record modification) -- kept deliberately separate from
                    "Actual Completion" below, which is the caller-supplied
                    completion date/time. These may legitimately differ. */}
                <div className="tdp-field"><span className="tdp-field-label">Last Updated</span><span className="tdp-field-value">{formatDateTime(task.updated_at)}</span></div>
              </div>

              {task.completed_at && (
                <div className="tdp-summary-group">
                  <span className="tdp-summary-heading">Completion</span>
                  <div className="tdp-field"><span className="tdp-field-label">Actual Completion</span><span className="tdp-field-value">{formatDateTime(task.completed_at)}</span></div>
                  {task.completed_by && (
                    <div className="tdp-field"><span className="tdp-field-label">Completed By</span><span className="tdp-field-value">{task.completed_by.name}</span></div>
                  )}
                  {task.completion_remark && (
                    <div className="tdp-field"><span className="tdp-field-label">Resolution / Work Done</span><span className="tdp-field-value">{task.completion_remark}</span></div>
                  )}
                </div>
              )}

              <div className="tdp-summary-group">
                <span className="tdp-summary-heading">Task Information</span>
                <div className="tdp-field"><span className="tdp-field-label">Unit</span><span className="tdp-field-value">{task.unit?.name || "—"}</span></div>
                {task.department && <div className="tdp-field"><span className="tdp-field-label">Department</span><span className="tdp-field-value">{task.department.name}</span></div>}
                {task.project && <div className="tdp-field"><span className="tdp-field-label">Project</span><span className="tdp-field-value">{task.project.name}</span></div>}
                {task.project_module && <div className="tdp-field"><span className="tdp-field-label">Module</span><span className="tdp-field-value">{task.project_module.name}</span></div>}
                <div className="tdp-field"><span className="tdp-field-label">Category</span><span className="tdp-field-value">{task.category?.name || "—"}</span></div>
                <div className="tdp-field"><span className="tdp-field-label">{subCategoryLabel}</span><span className="tdp-field-value">{renderNameList(subCategoryNames)}</span></div>                <div className="tdp-field"><span className="tdp-field-label">Task Type</span><span className="tdp-field-value">{task.task_type?.name || "IT Support"}</span></div>
              </div>

              <div className="tdp-summary-group">
                <span className="tdp-summary-heading">Quick Actions</span>
                <div className="tdp-quick-actions">
                  {isTaskOwner && (
                    <button type="button" className="tdp-quick-btn" onClick={() => setShowCompleteModal(true)}>
                      <FiCheckCircle /> {isCompleted ? "Edit Completion" : "Mark Completed"}
                    </button>
                  )}
                  <Can moduleKey="task_assignments" actionKey="reassign">
                    <button type="button" className="tdp-quick-btn" onClick={() => scrollToSection(assignmentRef)}>
                      <FiUserPlus /> Reassign
                    </button>
                  </Can>
                  <Can moduleKey="task_assignments" actionKey="reassign">
                    <button type="button" className="tdp-quick-btn" onClick={() => scrollToSection(assignmentRef)}>
                      <FiUsers /> Add Performer
                    </button>
                  </Can>
                  <Can moduleKey="tasks" actionKey="change_status">
                    <button type="button" className="tdp-quick-btn" onClick={() => { setShowStatusPanel(true); scrollToSection(statusRef); }}>
                      <FiRefreshCw /> Update Status
                    </button>
                  </Can>
                  <Can moduleKey="tasks" actionKey="add_remarks">
                    <button type="button" className="tdp-quick-btn" onClick={() => scrollToSection(remarksRef, remarkInputRef)}>
                      <FiPlusCircle /> Add Remark
                    </button>
                  </Can>
                  <Can moduleKey="tasks" actionKey="upload_attachment">
                    <button type="button" className="tdp-quick-btn" onClick={() => scrollToSection(attachmentsRef, fileInputRef)}>
                      <FiUpload /> Upload File
                    </button>
                  </Can>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* MOBILE STICKY ACTION BAR */}
      <div className="tdp-mobile-bar">
        {isTaskOwner && (
          <button type="button" onClick={() => setShowCompleteModal(true)}>
            <FiCheckCircle /> {isCompleted ? "Completion" : "Complete"}
          </button>
        )}
        <Can moduleKey="tasks" actionKey="add_remarks">
          <button type="button" onClick={() => scrollToSection(remarksRef, remarkInputRef)}><FiMessageSquare /> Comment</button>
        </Can>
        <Can moduleKey="tasks" actionKey="change_status">
          <button type="button" onClick={() => { setShowStatusPanel(true); scrollToSection(statusRef); }}><FiCheckCircle /> Status</button>
        </Can>
        <Can moduleKey="task_assignments" actionKey="reassign">
          <button type="button" onClick={() => scrollToSection(assignmentRef)}><FiUserCheck /> Assign</button>
        </Can>
      </div>

      <CompleteTicketModal
        show={showCompleteModal}
        task={task}
        onClose={() => setShowCompleteModal(false)}
        onCompleted={async () => {
          await Promise.all([load(), loadTransitions()]);
        }}
      />
    </div>
  );
}

function statusToneFromCode(code) {
  const cls = statusPillClass(code);
  const m = /tlp-pill-(\w+)/.exec(cls || "");
  return m ? m[1] : "gray";
}
